import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { LegalAction, MatchPublicView } from '../../api-client.js';
import { formatGoldenBallsEvent } from '../../lib/goldenballs/format-event.js';
import { EventLog } from '../EventLog.js';
import { MatchClock } from '../MatchClock.js';
import { BinOrWin } from './BinOrWin.js';
import { Debrief } from './Debrief.js';
import { FinalCeremony } from './FinalCeremony.js';
import { FinalRound } from './FinalRound.js';
import { FreeChat } from './FreeChat.js';
import { NominationSheet } from './NominationSheet.js';
import { QuickChatBuilder } from './QuickChatBuilder.js';
import { SeatPanel } from './SeatPanel.js';
import { ValueGrid } from './ValueGrid.js';
import { EliminationReveal, VoteReveal } from './VoteReveal.js';
import type { GbClaim, GbFinalChoice, GoldenBallsViewerState } from './types.js';

// ---------------------------------------------------------------------------
// Golden Balls arena
// ---------------------------------------------------------------------------
// Mobile-first, one viewport, no page scrollbars: the root is measured
// and pinned to the available height, every child is either shrink-0 or
// min-h-0 flex-1, and only inner panels scroll. On phones the secondary
// panels (table, pool, chat) live behind a tab strip so the primary
// decision always owns the screen with its action pinned at the bottom.

type Tab = 'play' | 'reserve' | 'talk';

/** The beat a chat line belongs to, for the talk log's dividers. */
function beatLabel(round: 1 | 2, phase: string): string {
  if (phase === 'pre_final') return 'last words';
  if (phase === 'final') return 'split or steal';
  return `round ${round}`;
}

const PHASE_COPY: Record<string, { title: string; hint: string }> = {
  nomination: { title: 'Declare', hint: 'Say what your hidden balls are.' },
  claim_reveal: { title: 'Claims', hint: 'Everyone declared at once.' },
  discussion: { title: 'Talk', hint: 'Work out who is lying.' },
  vote: { title: 'Vote', hint: 'Vote one player out. Everyone votes at once.' },
  elimination_reveal: { title: 'Reveal', hint: 'Every hand turns over.' },
  bin_or_win: { title: 'Bin or Win', hint: 'Build the jackpot.' },
  pre_final: { title: 'Last words', hint: 'Talk it out. Nothing is committed yet.' },
  final: { title: 'Split or Steal', hint: 'One decision left.' },
  ended: { title: 'Done', hint: 'The full debrief.' }
};

export function GoldenBallsMatch({
  view,
  match,
  legalActions,
  busy,
  onAct,
  error
}: {
  view: GoldenBallsViewerState;
  match: MatchPublicView;
  legalActions: LegalAction[];
  busy: boolean;
  onAct: (action: {
    actionKind: LegalAction['actionKind'];
    descriptorId?: string;
    choices?: Record<string, unknown>;
  }) => void | Promise<void>;
  error: string | null;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const height = useViewportFill(rootRef);
  const [tab, setTab] = useState<Tab>('play');
  const [selectedBallId, setSelectedBallId] = useState<string | null>(null);
  const [ceremonyDone, setCeremonyDone] = useState(false);
  // Were we watching when the balls opened? If we only ever saw the
  // finished match, skip the 3-2-1 and show the result straight away.
  const sawLiveFinal = useRef(false);
  if (view.phase === 'final' || view.phase === 'pre_final') sawLiveFinal.current = true;

  const yourSeatId = view.you?.seatId ?? null;
  const yourSeat = view.seats.find((seat) => seat.seatId === yourSeatId) ?? null;
  // From Bin or Win onwards the seat panels are dead weight: the balls
  // have left the seats and pooled, and the last three phases each own
  // the whole screen. This previously covered bin_or_win ONLY, so the
  // panels came straight back for the final and squeezed the Split /
  // Steal / Sting buttons until only SPLIT was reachable.
  const tableHidden =
    // The round-end reveal turns over every hidden ball for every seat
    // and prints claimed vs actual for each one — the seat panels above
    // it show the SAME balls again, and pushed the reveal itself
    // completely below the fold (measured at y=981 in a 980px window,
    // which is why "it is still not revealing the balls at the end of
    // the round"). The reveal is the phase; it gets the whole column.
    view.phase === 'elimination_reveal' ||
    view.phase === 'bin_or_win' ||
    view.phase === 'pre_final' ||
    view.phase === 'final' ||
    view.phase === 'ended';
  const lateGame =
    view.phase === 'bin_or_win' ||
    view.phase === 'pre_final' ||
    view.phase === 'final' ||
    view.phase === 'ended';
  const opponents = view.seats.filter(
    (seat) => seat.seatId !== yourSeatId && (!lateGame || seat.alive)
  );
  const copy = PHASE_COPY[view.phase] ?? { title: view.phase, hint: '' };
  const lastVoteReveal = view.voteReveals[view.voteReveals.length - 1];
  const lastElimination = view.eliminations[view.eliminations.length - 1];

  // A fresh phase should never leave you staring at a stale tab, or at a
  // ball selection that belongs to the previous deal.
  useEffect(() => {
    setTab('play');
    setSelectedBallId(null);
  }, [view.phase, view.round]);

  const act = (descriptorId: string, choices: Record<string, unknown> = {}) =>
    onAct({ actionKind: 'take_action', descriptorId, choices });

  const canVote = view.phase === 'vote' && view.vote?.yourVote == null && !!yourSeat?.alive;
  const skipLabel =
    view.phase === 'claim_reveal' || view.phase === 'elimination_reveal'
      ? 'Move on'
      : 'Skip the wait';

  // Nomination: the selected hidden ball defaults to the first one you
  // have not declared, and follows you as you declare them.
  const nominating = view.phase === 'nomination' && !!yourSeat?.alive;
  const activeBallId = nominating
    ? (selectedBallId && yourSeat!.backRow.some((ball) => ball.ballId === selectedBallId)
        ? selectedBallId
        : (yourSeat!.backRow.find((ball) => !ball.committed)?.ballId ??
           yourSeat!.backRow[0]?.ballId ??
           null))
    : null;
  const undeclared = yourSeat?.backRow.some((ball) => !ball.committed) ?? false;
  const seatLabels = Object.fromEntries(
    view.seats.map((seat) => [seat.seatId, seat.label])
  );

  // Your buttons. Rendered INSIDE your seat panel (see SeatPanel's
  // `actions` slot) so they use the width the player zone already has
  // instead of stacking full-width rows on top of the balls.
  const waitingLabels = view.skip.waitingOn
    .map((seatId) => view.seats.find((s) => s.seatId === seatId)?.label ?? seatId)
    .join(', ');
  // Never in the final: see the engine's applyReady guard. A ready
  // button there commits SPLIT for you.
  const seatActions =
    yourSeat?.alive && view.phase !== 'ended' && view.phase !== 'final' ? (
      <>
        {/* Short on a phone, full at sm+. These sit on the seat panel's
            name line now, and "Declare the rest honestly" alone is wider
            than a 360px screen has spare there. The long wording stays
            as the tooltip and returns as soon as there is room. */}
        <button
          type="button"
          disabled={busy || view.skip.youAreReady}
          onClick={() => act('gb:ready')}
          title={view.skip.youAreReady ? 'Waiting for the others' : skipLabel}
          className="ec-btn min-h-[2.25rem] whitespace-nowrap px-2 text-[0.65rem] disabled:opacity-40 sm:min-h-[2.5rem] sm:px-3 sm:text-xs"
        >
          {view.skip.youAreReady ? 'Ready ✓' : (
            <>
              <span className="sm:hidden">Skip</span>
              <span className="hidden sm:inline">{skipLabel}</span>
            </>
          )}
        </button>
        {nominating && undeclared && (
          <button
            type="button"
            disabled={busy}
            onClick={() => act('gb:declare_honest')}
            title="Declare every remaining hidden ball at its true value"
            className="ec-btn min-h-[2.25rem] whitespace-nowrap px-2 text-[0.65rem] disabled:opacity-40 sm:min-h-[2.5rem] sm:px-3 sm:text-xs"
          >
            <span className="sm:hidden">All honest</span>
            <span className="hidden sm:inline">Declare the rest honestly</span>
          </button>
        )}
        {view.skip.waitingOn.length > 0 && view.skip.readySeatIds.length > 0 && (
          <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
            waiting on {waitingLabels}
          </span>
        )}
      </>
    ) : null;

  return (
    <div
      ref={rootRef}
      style={height ? { height } : undefined}
      className="flex flex-col gap-2 overflow-hidden"
    >
      {/* Header: phase, round, clock. Always one line on a phone. */}
      <header className="flex shrink-0 flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="font-display text-base font-semibold uppercase tracking-wide text-ec-face">
          {copy.title}
        </h2>
        <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
          round {view.round} · {view.config.ballMode === 'threeBall' ? '3-ball' : '2-ball'} ·{' '}
          {view.config.anonymityMode}
        </span>
        <div className="ml-auto flex items-baseline gap-2">
          {match.match.status === 'in_progress' && (
            <MatchClock deadlineAt={match.decisionWindow?.deadlineAt} />
          )}
        </div>
      </header>

      {error && (
        <p className="shrink-0 rounded border border-ec-oxblood-2/60 bg-ec-oxblood-deep/20 px-2 py-1 font-serif text-xs text-ec-face">
          {error}
        </p>
      )}

      {/* Mobile tab strip. Desktop shows everything side by side. */}
      <nav className="flex shrink-0 gap-1 lg:hidden">
        {(['play', 'reserve', 'talk'] as Tab[]).map((candidate) => (
          <button
            key={candidate}
            type="button"
            onClick={() => setTab(candidate)}
            aria-pressed={tab === candidate}
            className={[
              'min-h-[2.25rem] flex-1 rounded border font-display text-[0.6rem] uppercase tracking-wide transition',
              tab === candidate
                ? 'border-ec-brass bg-ec-brass/15 text-ec-brass'
                : 'border-ec-line text-ec-bone-dim'
            ].join(' ')}
          >
            {candidate}
          </button>
        ))}
      </nav>

      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,24rem)]">
        {/* Primary column — the decision. */}
        {/* `overflow-y-auto` is the safety net, not the plan. Every
            phase is laid out to fit, but the arena root is
            height-pinned with overflow-hidden, so anything that DOES
            overflow is silently clipped rather than scrolled — and a
            clipped button is an unreachable one. That is how STEAL and
            STING disappeared. An inner scroll here keeps every action
            reachable at any viewport while the PAGE still never
            scrolls, which is the actual rule. */}
        <main
          className={`flex min-h-0 flex-col gap-2 overflow-y-auto ${
            tab === 'play' ? '' : 'hidden lg:flex'
          }`}
        >
          <p className="shrink-0 font-serif text-xs text-ec-bone-dim">{copy.hint}</p>

          {/* THE TABLE, in two separately-budgeted pieces.
              OPPONENTS absorb the leftover height and are the only part
              allowed to scroll. YOUR HAND is shrink-0 and lives OUTSIDE
              that scroller, so it is on screen at every viewport in
              every phase — putting it inside the same scroll box is why
              the owner had to scroll to see their own balls while a void
              sat under the picker. */}
          {/* `justify-start`, not centre: centring a scroll container
              whose content is taller than it pushes the overflow off
              BOTH ends, so the opponents' balls were sliced in half at
              the top with no way to scroll up to them. */}
          {!tableHidden && opponents.length > 0 && (
            <div className="flex min-h-[3.5rem] flex-1 flex-col justify-start overflow-y-auto">
              <div
                className="grid gap-2"
                style={{
                  gridTemplateColumns: `repeat(${Math.max(1, opponents.length)}, minmax(0, 1fr))`
                }}
              >
                {opponents.map((seat) => (
                  <SeatPanel
                    key={seat.seatId}
                    seat={seat}
                    committed={
                      view.phase === 'nomination'
                        ? view.nomination?.committedSeats[seat.seatId]
                        : undefined
                    }
                    voted={view.phase === 'vote' ? view.vote?.votedSeats[seat.seatId] : undefined}
                    isVoteTarget={view.vote?.yourVote === seat.seatId}
                    identity={view.identities[seat.seatId]}
                    compact
                  />
                ))}
              </div>
            </div>
          )}

          {!tableHidden && yourSeat && (
            <div className="shrink-0">
              {/* Your hand, once, always visible. During nomination its
                  hidden balls ARE the declare control, and your buttons
                  sit beside them inside this panel. */}
              <SeatPanel
                seat={yourSeat}
                committed={
                  view.phase === 'nomination'
                    ? view.nomination?.committedSeats[yourSeat.seatId]
                    : undefined
                }
                voted={view.phase === 'vote' ? view.vote?.votedSeats[yourSeat.seatId] : undefined}
                identity={view.identities[yourSeat.seatId]}
                onSelectBall={nominating ? setSelectedBallId : undefined}
                selectedBallId={activeBallId}
                size="lg"
                actions={seatActions}
              />
            </div>
          )}

          {/* When the table is hidden (Bin or Win onwards) the skip
              controls have no panel to live in, so they get a slim row
              of their own — there is nothing else competing for it. */}
          {tableHidden && seatActions && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">{seatActions}</div>
          )}

          {/* THE VOTE, pinned. It used to be a button inside each
              opponent panel — inside the scroller — where at 360x640 it
              rendered past the clip line and could not be tapped at all.
              A decision this size never lives somewhere that can be
              scrolled out from under you. */}
          {canVote && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
                Vote one out
              </span>
              {view.seats
                .filter((seat) => seat.alive && seat.seatId !== yourSeatId)
                .map((seat) => (
                  <button
                    key={seat.seatId}
                    type="button"
                    disabled={busy}
                    onClick={() => act(`gb:vote:${seat.seatId}`)}
                    className="ec-btn min-h-[2.75rem] flex-1 px-2 text-xs disabled:opacity-40"
                  >
                    {seat.label}
                  </button>
                ))}
            </div>
          )}

          {view.phase === 'nomination' && yourSeat && (
            <NominationSheet
              seat={yourSeat}
              selectedBallId={activeBallId}
              legalValues={view.legalClaimValues}
              busy={busy}
              onClaim={(ballId: string, value: GbClaim) =>
                act(`gb:claim:${ballId}`, { value })
              }
            />
          )}


          {view.phase === 'vote' && view.vote?.yourVote && (
            <p className="shrink-0 text-center font-display text-xs uppercase tracking-wide text-ec-bone-dim">
              Vote locked. Nobody sees it until everyone has voted.
            </p>
          )}

          {/* The ballots and the hands SIDE BY SIDE on a wide screen.
              Stacked, the reveal started 525px down a 980px window and
              still ran off the bottom, while half the width sat empty. */}
          {view.phase === 'elimination_reveal' && (
            <div className="grid min-h-0 flex-1 gap-2 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
              {lastVoteReveal && (
                <div className="min-h-0 overflow-y-auto">
                  <VoteReveal reveal={lastVoteReveal} seats={view.seats} />
                </div>
              )}
              {lastElimination && (
                <div className="min-h-0 overflow-y-auto">
                  <EliminationReveal elimination={lastElimination} seats={view.seats} />
                </div>
              )}
            </div>
          )}

          {view.phase === 'bin_or_win' && (
            <BinOrWin
              view={view}
              seats={view.seats}
              yourSeatId={yourSeatId}
              busy={busy}
              onPlace={(slot, dest) => act(`gb:place:s${slot}:${dest}`)}
              onSkipToEnd={() => act('gb:skip_to_end')}
            />
          )}

          {(view.phase === 'pre_final' || view.phase === 'final') && (
            <FinalRound
              view={view}
              seats={view.seats}
              yourSeatId={yourSeatId}
              busy={busy}
              onChoose={(choice: GbFinalChoice) => act(`gb:final:${choice}`)}
            />
          )}

          {/* THE REVEAL, then the numbers. The engine resolves the final
              and moves to 'ended' in the SAME transition, so there is no
              viewer state with phase 'final' AND a result — a ceremony
              rendered on the final phase is unreachable in a real match
              no matter how well it works in a fixture. It belongs here,
              in front of the debrief. */}
          {view.phase === 'ended' &&
            view.debrief?.finalResult &&
            !ceremonyDone &&
            sawLiveFinal.current && (
            <FinalCeremony
              seats={view.seats}
              jackpot={view.debrief.jackpot}
              choices={view.debrief.finalResult.choices}
              payouts={view.debrief.finalResult.payouts}
              destroyed={view.debrief.finalResult.destroyed}
              explanation={view.debrief.finalResult.explanation}
              // Only play the countdown for people who were HERE when it
              // landed. Opening a finished match shows the result at once.
              animate
              onDone={() => setCeremonyDone(true)}
            />
          )}

          {/* The debrief. Shown once the reveal is done — or immediately
              for anyone who opened a match that had already finished,
              who should not have to sit through (or click past) a
              countdown for a result decided before they arrived. */}
          {view.phase === 'ended' &&
            view.debrief &&
            (ceremonyDone || !view.debrief.finalResult || !sawLiveFinal.current) && (
              <Debrief
                debrief={view.debrief}
                seats={view.seats}
                ballMode={view.config.ballMode}
              />
            )}
        </main>

        {/* Secondary column — reserve, then talk: quick chat AND the
            match log together. They used to be mutually exclusive tabs,
            so opening chat hid every piece of commentary in the game. */}
        <aside
          className={`flex min-h-0 flex-col gap-2 ${
            tab === 'play' ? 'hidden lg:flex' : 'flex'
          }`}
        >
          {/* On a PHONE the open tab owns the whole column. The
              `max-h-[38%]` is a desktop rule for stacking the reserve
              above the talk — applying it on mobile capped the reserve
              at a third of the screen and left the rest black, which is
              most of what "wasting space" meant. */}
          <section
            className={`min-h-0 overflow-y-auto rounded-lg border border-ec-line bg-ec-panel/50 p-2 lg:max-h-[38%] lg:shrink-0 ${
              tab === 'reserve' ? 'flex-1' : 'hidden lg:block'
            }`}
          >
            <ValueGrid
              entries={view.valueGrid.cashValues}
              killers={view.valueGrid.killers}
              round={view.round}
              newDraws={view.valueGrid.newDraws}
              newDrawCandidates={view.valueGrid.newDrawCandidates}
              compact
            />
          </section>

          <section
            className={`flex min-h-0 flex-[2] flex-col gap-2 rounded-lg border border-ec-line bg-ec-panel/50 p-2 ${
              tab === 'talk' ? '' : 'hidden lg:flex'
            }`}
          >
            {/* THREE budgeted sections. The phrase builder used to be
                shrink-0, and a seven-phrase group is ~300px tall, so it
                squeezed the commentary underneath it to nothing —
                "the final chat tab hides all the commentary". Each part
                now has a floor AND a ceiling and scrolls inside itself. */}
            <h3 className="shrink-0 font-display text-xs uppercase tracking-wide text-ec-bone-dim">
              Table talk
            </h3>
            {/* Grouped by the beat it was said in. Ungrouped, the whole
                game's talk ran together, so a round-one "you're sitting
                on a killer" sat directly above the final and read as if
                a bot were still arguing about balls that no longer
                exist. Dimmed once its beat has passed. */}
            <ol className="min-h-[3.5rem] flex-1 overflow-y-auto">
              {view.chatLog.length === 0 && (
                <li className="font-serif text-xs text-ec-bone-dim">
                  {view.phase === 'nomination' || view.phase === 'vote'
                    ? 'Chat is closed while everyone decides.'
                    : 'Nobody has said anything yet.'}
                </li>
              )}
              {view.chatLog.map((entry, index) => {
                const seat = view.seats.find((candidate) => candidate.seatId === entry.seatId);
                const previous = view.chatLog[index - 1];
                const newBeat =
                  !previous || previous.round !== entry.round || previous.phase !== entry.phase;
                const current = entry.round === view.round && entry.phase === view.phase;
                return (
                  <li key={entry.seq}>
                    {newBeat && (
                      <p className="mt-1.5 flex items-center gap-2 font-display text-[0.5rem] uppercase tracking-widest text-ec-bone-dim first:mt-0">
                        <span className="shrink-0">{beatLabel(entry.round, entry.phase)}</span>
                        <span className="h-px flex-1 bg-ec-line/60" />
                      </p>
                    )}
                    <p className={current ? '' : 'opacity-60'}>
                      <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-brass">
                        {seat?.label ?? entry.seatId}
                      </span>{' '}
                      <span className="font-serif text-xs text-ec-face">{entry.text}</span>
                    </p>
                  </li>
                );
              })}
            </ol>
            {(view.phase === 'discussion' ||
              view.phase === 'pre_final' ||
              view.phase === 'final') &&
              yourSeat?.alive && (
                <div className="max-h-[45%] shrink-0 overflow-y-auto border-t border-ec-line pt-2">
                  {/* A pact offered TO YOU is a question, not a log
                      line. Answer it here or it scrolls away unanswered. */}
                  {view.inbox?.pactOffers.map((offer) => {
                    const from =
                      view.seats.find((s) => s.seatId === offer.fromSeatId)?.label ??
                      offer.fromSeatId;
                    return (
                      <div
                        key={offer.fromSeatId}
                        className="mb-2 rounded border border-ec-brass/60 bg-ec-brass/10 p-2"
                      >
                        <p className="font-serif text-xs text-ec-face">
                          <strong className="text-ec-brass">{from}</strong>{' '}
                          {offer.conditional
                            ? 'wants a split pact — if you vote with them.'
                            : 'offered you a straight split pact.'}
                        </p>
                        <div className="mt-1.5 flex gap-2">
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              act('gb:chat:pact_accept', { targetSeatId: offer.fromSeatId })
                            }
                            className="ec-btn ec-btn--gold min-h-[2.5rem] flex-1 text-xs disabled:opacity-40"
                          >
                            Deal
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              act('gb:chat:pact_reject', { targetSeatId: offer.fromSeatId })
                            }
                            className="ec-btn min-h-[2.5rem] flex-1 text-xs disabled:opacity-40"
                          >
                            No deal
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <QuickChatBuilder
                    actions={legalActions}
                    seats={view.seats}
                    yourSeatId={yourSeatId}
                    busy={busy}
                    onSay={(descriptorId, choices) => act(descriptorId, choices)}
                  />
                </div>
              )}

            {/* The commentary. Sits WITH the chat, not behind it, and
                keeps a floor so the builder above can never bury it. */}
            <div className="flex min-h-[5rem] flex-1 flex-col border-t border-ec-line pt-2">
              <EventLog
                matchId={match.match.id}
                seatLabels={seatLabels}
                layout="fill"
                format={formatGoldenBallsEvent}
              />
            </div>

            {/* Free chat, when the lobby enabled it. Kept visually
                separate from quick chat because only quick chat has
                game meaning — a pact typed here binds nothing. */}
            {view.config.freeChatEnabled && (
              <div className="flex min-h-0 flex-1 flex-col border-t border-ec-line pt-2">
                <p className="shrink-0 font-display text-[0.55rem] uppercase tracking-wide text-ec-bone-dim">
                  Free chat · not read by the game
                </p>
                <FreeChat
                  matchId={match.match.id}
                  canPost={
                    !!yourSeat?.alive &&
                    view.phase !== 'nomination' &&
                    view.phase !== 'vote'
                  }
                  refreshKey={view.chatLog.length + (match.eventCount ?? 0)}
                />
              </div>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

/**
 * Pin the arena to the space actually available below the header, so
 * the page itself never scrolls (the "game, not web app" rule).
 * Uses window.innerHeight rather than visualViewport.height so pinching
 * to read a ball doesn't shrink the board, but listens to
 * visualViewport resizes so the mobile URL bar sliding away re-measures.
 *
 * A window resize is NOT the only thing that moves us. Anything above
 * the arena changing height — a header wrapping to two lines at a
 * narrow width, an error banner appearing, a webfont landing — shifts
 * our top without any resize event, and the stale pinned height then
 * shows up as exactly the page scrollbar this hook exists to prevent.
 * A ResizeObserver on the ancestors catches all of it.
 */
function useViewportFill(ref: React.RefObject<HTMLElement | null>): number | null {
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const node = ref.current;
      if (!node) return;
      const top = node.getBoundingClientRect().top;

      // Sum only what GENUINELY needs room below us: our own and our
      // ancestors' bottom margins, each ancestor's bottom padding and
      // border, and the height of any real FOLLOWING SIBLING at each
      // level.
      //
      // What this must never count is "the parent box is taller than we
      // are". That was the old `belowUs` term, and it is the space we
      // are trying to claim, not space something else occupies —
      // subtracting it shrinks us, which makes the gap bigger, which
      // shrinks us again. Every re-measure walked the arena down (619px
      // → 447px in a 651px slot) and left the dead space the owner saw
      // under the board.
      const px = (value: string) => Number.parseFloat(value || '0') || 0;
      let bottomInset = 0;
      let current: HTMLElement | null = node;
      while (current && current !== document.body) {
        bottomInset += px(getComputedStyle(current).marginBottom);
        for (
          let sibling = current.nextElementSibling;
          sibling;
          sibling = sibling.nextElementSibling
        ) {
          const style = getComputedStyle(sibling);
          // Out-of-flow siblings overlay us; they do not take our room.
          if (style.display === 'none') continue;
          if (style.position === 'absolute' || style.position === 'fixed') continue;
          bottomInset +=
            sibling.getBoundingClientRect().height + px(style.marginTop) + px(style.marginBottom);
        }
        const parent: HTMLElement | null = current.parentElement;
        if (!parent) break;
        const parentStyle = getComputedStyle(parent);
        bottomInset += px(parentStyle.paddingBottom) + px(parentStyle.borderBottomWidth);
        current = parent;
      }

      const next = Math.max(280, window.innerHeight - top - bottomInset);
      // Only react to a real change: our own height feeds back into the
      // ancestors we observe, and a sub-pixel wobble would loop.
      setHeight((current) => (current !== null && Math.abs(current - next) < 1 ? current : next));
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    window.visualViewport?.addEventListener('resize', measure);

    // Watch everything between us and the document for height changes.
    const observer = new ResizeObserver(measure);
    let current: HTMLElement | null = ref.current;
    while (current && current !== document.body) {
      if (current.parentElement) observer.observe(current.parentElement);
      current = current.parentElement;
    }
    observer.observe(document.body);
    // …and the viewport itself. The `resize` event is the usual signal,
    // but it is not guaranteed to reach us (it does not fire under CDP
    // metrics emulation, and the ancestors we observe are all
    // content-height boxes that need not change when the window does).
    // Without this a rotation leaves the previous orientation's pinned
    // height behind — measured as 803px in a 511px slot, i.e. 316px of
    // page scrollbar, which is the one thing this hook exists to stop.
    observer.observe(document.documentElement);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, [ref]);

  return height;
}
